import { type KeyboardEvent, useEffect, useRef, useState } from 'react'

import { type LibraryFolder, fetchLibrary } from '../../api/library'
import type { SearchHit } from '../../api/search'
import { navigate } from '../../frame/route'
import { Button } from '../../ui/Control'
import { Searching } from './Searching'
import { SEARCH_AREA, pathTo, whereItLives } from './place'

/**
 * The search as a layer over whatever somebody was doing.
 *
 * It is the way the search is *meant* to be used: ⌘K, type, arrow down, Enter, and you are in the
 * document — without having gone anywhere first, and without losing the place you were in. Escape puts
 * you back exactly where you were, focus and all; **which control that was is remembered by the frame**,
 * because the frame is what opened this and is therefore the only thing that saw the focus before the
 * field here took it.
 *
 * **The layer has no address of its own**, and that is a deliberate departure from "the address is the
 * truth" everywhere else in MyLib. A layer that rewrote the address while somebody typed would either
 * fill the browser's history with half-typed words or lie about what the back button does. What is worth
 * sending to somebody is the detailed search behind it, and the link at the bottom leads there with
 * whatever has been typed so far.
 *
 * On a phone it fills the screen, which makes it the same thing as the detailed search rather than a
 * cramped version of it.
 */
export interface SpotlightProps {
  readonly onClose: () => void
}

export function Spotlight({ onClose }: SpotlightProps) {
  const panel = useRef<HTMLDivElement>(null)
  const [line, setLine] = useState('')
  const [folders, setFolders] = useState<readonly LibraryFolder[]>([])

  /* The folders, so a hit can say where it lives. Fetched when the layer opens and not before. */
  useEffect(() => {
    const controller = new AbortController()
    fetchLibrary(controller.signal)
      .then((library) => {
        setFolders(library.folders)
      })
      .catch(() => {
        // A hit without the name of its folder is still a hit. Nothing to say about this.
      })
    return () => {
      controller.abort()
    }
  }, [])

  const open = (hit: SearchHit) => {
    const to = whereItLives(hit)
    if (to === null) {
      return
    }
    navigate(to.area, to.path)
    onClose()
  }

  /**
   * Keeps the focus inside the layer.
   *
   * A dialog whose focus can walk out into the page behind it is a dialog a screen reader reads the wrong
   * half of. Tab inside the field is taken by the ghost completion first; everything else wraps.
   */
  const trap = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || panel.current === null) {
      return
    }
    const reachable = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (element) => element.offsetParent !== null,
    )
    const first = reachable.at(0)
    const last = reachable.at(-1)
    if (first === undefined || last === undefined) {
      return
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className="spotlight"
      onMouseDown={(event) => {
        // A click beside the layer closes it. Escape and the close control do the same thing, so
        // nothing here is reachable only by knowing that this works.
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className="spotlight__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        ref={panel}
        onKeyDown={trap}
      >
        <Searching
          shape="compact"
          line={line}
          order="relevance"
          onLine={setLine}
          onOpen={open}
          onLeave={onClose}
          folders={folders}
          focused
          footer={
            <div className="spotlight__ways">
              <Button
                tone="bare"
                onClick={() => {
                  navigate(SEARCH_AREA, pathTo({ line, order: 'relevance' }))
                  onClose()
                }}
              >
                Detailed search
              </Button>
              <Button tone="bare" icon="close" iconOnlyLabel="Close the search" onClick={onClose} />
            </div>
          }
        />
      </div>
    </div>
  )
}

/** What Tab may reach. Enough for the layer, which holds a field, a list and two controls. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, [tabindex="0"]'
